/* eslint-disable global-require */
/* eslint-disable import/no-dynamic-require */
import React, { useState } from 'react';
import { Dialog } from 'primereact/dialog';
import moment from 'moment';
import './Timeline.css';
import { Add, Remove, RestartAlt } from '@mui/icons-material';
import { Divider } from 'primereact/divider';
import { allColorList, defaultColor } from '../../constants/ColorList';
import {
  ILZTimelineData,
  ILZTimelineItem,
  TimelineDataLZ
} from '../../types/timeline/timelineInterface';
import Strings from '../../i18n';
import { HtmlTooltip } from '../commons/TooltipWithHtml/tooltipWithHTML';
import {
  ILZTimelineDeviceData,
  TimelineData
} from '../../types/deviceConnection';
import { ControllerConnectionStatesEnum } from '../../constants/enums/deviceEnums';

export function getControllerStateLabel(
  state: ControllerConnectionStatesEnum
): string {
  switch (state) {
    case ControllerConnectionStatesEnum.CONNECTED:
      return 'Online';
    case ControllerConnectionStatesEnum.DISCONNECTED:
      return 'Offline';
    case ControllerConnectionStatesEnum.UNKNOWN:
      return 'No Data';
    default:
      return 'No Data';
  }
}

function isTimelineDeviceData(data: any) {
  return 'connectionState' in data;
}

export const Timeline = ({
  data,
  showControls
}: {
  data: TimelineData | TimelineDataLZ;
  showControls: boolean;
}) => {
  const [selectedEvent, setSelectedEvent] = useState<ILZTimelineItem | null>(
    null
  );

  const [modalVisible, setModalVisible] = useState(false);
  const listOfColors = allColorList;
  const [zoomLevels, setZoomLevels] = useState<{ [key: string]: number }>({});

  const dayInMilliseconds = 24 * 60 * 60 * 1000;
  const hours = Array.from({ length: 13 }, (_, i) => i * 2);

  const calculateWidth = (startDate: number, endDate: number) => {
    const duration = endDate - startDate;
    const percentage = (duration / dayInMilliseconds) * 100;
    return `max(${percentage}%, 10px)`;
  };

  const calculateLeft = (startDate: number) => {
    const startPercentage =
      ((startDate % dayInMilliseconds) / dayInMilliseconds) * 100;
    return `${startPercentage}%`;
  };

  const renderTimeMarker = (hour: number) => {
    if (hour === 0 || hour === 24) return Strings.timeline.twelveAM;
    if (hour === 12) return Strings.timeline.twelvePM;
    return hour < 12 ? `${hour}AM` : `${hour - 12}PM`;
  };

  const distributeEventsInRows = (events: any[]) => {
    return events.reduce((acc: any[][], event) => {
      const rowIndex = acc.findIndex(
        (row) =>
          !row.some(
            (e) => parseFloat(e.epochEndDate) > parseFloat(event.epochStartDate)
          )
      );

      if (rowIndex === -1) {
        acc.push([event]);
      } else {
        acc[rowIndex].push(event);
      }

      return acc;
    }, []);
  };

  const handleZoom = (date: string, change: number) => {
    setZoomLevels((prevZoomLevels) => ({
      ...prevZoomLevels,
      [date]: Math.min(Math.max((prevZoomLevels[date] || 1) + change, 0.5), 3)
    }));
  };

  const resetZoom = (date: string) => {
    setZoomLevels((prevZoomLevels) => ({
      ...prevZoomLevels,
      [date]: 1
    }));
  };

  const returnImage = (color: string) => {
    if (color) {
      const image = require(`../../assets/images/colors/${color}`);
      return image;
    }
    return 'none';
  };

  const getStylesFromColorListAndStatus = (
    color: string,
    status: string,
    colorId?: number,
    isDeviceData?: boolean,
    event?: any
  ) => {
    if (isDeviceData) {
      if (event.connectionState === ControllerConnectionStatesEnum.CONNECTED) {
        return {
          backgroundColor: 'green',
          color: 'white'
        };
      }
      return {
        backgroundColor: 'black',
        color: 'white'
      };
    }

    if (color === 'No Data') {
      return {
        backgroundColor: 'black',
        color: 'white'
      };
    }
    if (status === 'Off' || color === 'Off') {
      return {
        backgroundColor: '#575757',
        color: 'white'
      };
    }
    if (colorId) {
      const colorObject = listOfColors.allColors.find((c) => c.id === colorId);
      return {
        backgroundColor: colorObject?.color,
        color: 'white',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundImage: colorObject?.icon
          ? `url(${returnImage(colorObject?.icon)})`
          : 'none'
      };
    }
    const colorObject =
      listOfColors.allColors.find(
        (c) => c.name.toLowerCase() === color?.toLowerCase() || ''
      ) || defaultColor;
    return {
      backgroundColor: colorObject?.color,
      color: 'white',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      backgroundImage: colorObject?.icon
        ? `url(${returnImage(colorObject?.icon)})`
        : 'none'
    };
  };

  return (
    <div className="timeline-container">
      {Object.keys(data).map((date) => {
        const eventRows = distributeEventsInRows(data[date]);

        return (
          <div key={date} className="timeline-day">
            <div className="row justify-content-end px-4 align-items-center">
              <div className="col">
                <h3 className="title-date">{date}</h3>
              </div>
              <div className="col text-end">
                {showControls && (
                  <div className="zoom-controls text-end">
                    <button type="button" onClick={() => handleZoom(date, 0.2)}>
                      <Add fontSize="small" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleZoom(date, -0.2)}
                    >
                      <Remove fontSize="small" />
                    </button>
                    <button type="button" onClick={() => resetZoom(date)}>
                      <RestartAlt fontSize="small" />
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="timeline-wrapper">
              <div
                className="timeline-background"
                style={{
                  transform: `scaleX(${zoomLevels[date] || 1})`,
                  transformOrigin: 'left',
                  minWidth: `${(zoomLevels[date] || 1) * 100}%`
                }}
              >
                {eventRows.map((row, rowIndex) => (
                  <div
                    className="timeline-track"
                    key={rowIndex}
                    style={{ top: rowIndex * 35 }}
                  >
                    {row.map((event: any, index: number) => {
                      const status =
                        event.changedData?.Status ??
                        (event.lightingStatus === 'Off' ? 'Off' : '');

                      return (
                        <HtmlTooltip
                          key={index}
                          title={
                            <>
                              <p className="mb-0">{event.changeReason}</p>
                              <Divider className="mb-1 mt-0" />
                              <p className="mb-0">
                                {`${moment(event.startDate).format(
                                  'HH:mm:ss'
                                )} to ${moment(event.endDate).format(
                                  'HH:mm:ss'
                                )}`}
                              </p>
                              <p className="mb-0">
                                {`Duration: ${moment
                                  .utc(
                                    moment(event.endDate).diff(event.startDate)
                                  )
                                  .format('HH:mm')} hrs`}
                              </p>
                              {isTimelineDeviceData(event) ? (
                                <p className="mb-0">
                                  {event.connectionState ===
                                  ControllerConnectionStatesEnum.CONNECTED
                                    ? 'Online'
                                    : 'Offline'}
                                </p>
                              ) : (
                                <p className="mb-0">
                                  Changes: {JSON.stringify(event.changedData)}
                                </p>
                              )}
                            </>
                          }
                          arrow
                          onClick={() => {
                            if (isTimelineDeviceData(event)) {
                              return;
                            }
                            setSelectedEvent(event);
                            setModalVisible(true);
                          }}
                        >
                          <div
                            className="timeline-item"
                            style={{
                              width: calculateWidth(
                                parseFloat(event.epochStartDate),
                                parseFloat(event.epochEndDate)
                              ),
                              left: calculateLeft(
                                parseFloat(event.epochStartDate)
                              ),
                              ...getStylesFromColorListAndStatus(
                                event.color,
                                status,
                                event.colorId,
                                isTimelineDeviceData(event),
                                event
                              )
                            }}
                          >
                            <span className="timeline-label-vertical">
                              {isTimelineDeviceData(event)
                                ? getControllerStateLabel(event.connectionState)
                                : event.changeReason}
                            </span>
                          </div>
                        </HtmlTooltip>
                      );
                    })}
                  </div>
                ))}
                {hours.map((hour) => (
                  <div
                    key={hour}
                    className="time-marker"
                    style={{ left: `${(hour / 24) * 100}%` }}
                  >
                    {renderTimeMarker(hour)}
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      })}

      <Dialog
        visible={modalVisible}
        onHide={() => setModalVisible(false)}
        header="Event Details"
        style={{ width: '50vw' }}
      >
        {selectedEvent && (
          <div>
            {[
              {
                label: Strings.timeline.lightZone,
                value: `${selectedEvent.lightZoneName} (${selectedEvent.lightZoneId})`,
                key: 'lightZoneName'
              },
              {
                label: Strings.timeline.status,
                value: selectedEvent.lightingStatus,
                key: 'Status'
              },
              {
                label: Strings.timeline.color,
                value: selectedEvent.color,
                key: 'Color'
              },
              {
                label: Strings.timeline.brightness,
                value: selectedEvent.brightness,
                key: 'Brightness'
              },
              {
                label: Strings.timeline.changeReason,
                value: selectedEvent.changeReason,
                key: 'changeReason'
              },
              {
                label: Strings.timeline.startDate,
                value: moment(selectedEvent.startDate).format(
                  'DD/MM/YYYY HH:mm:ss'
                ),
                key: 'startDate'
              },
              {
                label: Strings.timeline.endDate,
                value: moment(selectedEvent.endDate).format(
                  'DD/MM/YYYY HH:mm:ss'
                ),
                key: 'endDate'
              }
            ].map((item) => {
              const isChanged =
                selectedEvent.changedData[
                  item.key as keyof typeof selectedEvent.changedData
                ] === item.value;
              return (
                <p
                  key={item.key}
                  className={`timeline-item-changed ${
                    isChanged ? 'changed' : ''
                  }`}
                >
                  <strong>{item.label}:</strong> {item.value}
                </p>
              );
            })}
          </div>
        )}
      </Dialog>
    </div>
  );
};
